interface DataConfig {
  key: string;
  graph: string;
  transform: (data: any, fullData?: any) => any;
}

export const previewParticipantLevelSentimentAnalyticsDataConfigs: DataConfig[] = [
  {
    key: 'performance_quadrant',
    graph: 'ScatterChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'performance_quadrant');
      const scatterData = metric?.data;
      
      if (!scatterData) {
        return { payload: [], details: {} };
      }

      // Add tooltip info to details
      const details = {
        tooltipDescription: 'Segment participants based on quality and volume metrics to identify performance quadrants.',
        points: [
          `Participants are categorized into quadrants based on their quality scores and call volumes.`,
          `Quadrant thresholds are determined by median values of quality and volume. Quadrant Thresholds : Quality: ${scatterData?.quadrant_thresholds?.quality_mid || 0}, Volume: ${scatterData?.quadrant_thresholds?.volume_mid || 0}.`,
          `Quadrants help in strategizing training and resource allocation.`
        ]
      };

      // Transform points data
      const points = (scatterData?.points || []).map((point: any) => ({
        participant_id: point?.participant_id || '',
        name: point?.name || '',
        quality_score: point?.quality_score || 0,
        call_volume: point?.call_volume || 0,
        quadrant: point?.quadrant?.toLowerCase() || 'normal',
        color: point?.color || '#6B7280',
        tooltip_text: point?.tooltip || '',
      }));

      return { payload: points, details };
    }
  },
  {
    key: 'attention_required',
    graph: 'AlertView',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'attention_required');
      const alertData = metric?.data;
      
      if (!alertData) {
        return { payload: [], details: {} };
      }

      // Transform alert details for AlertView component
      const alertsList = (alertData?.details || []).map((alert: any) => ({
        participant_id: alert?.participant_id || '',
        title: `${alert?.name || 'Unknown'} - ${alert?.reason || 'No reason provided'}`,
        detail: alert?.detail || '',
        description: alert?.metric_value || '',
        severity: alert?.severity || 'info',
        alert_title: alert?.reason || '',
        alert_detail: `${alert?.detail || ''} (${alert?.trend || 'stable'} for ${alert?.weeks_in_state || 0} week(s))`,
        suggested_actions: alert?.suggested_actions || [],
        metric_value: alert?.metric_value || '',
        trend: alert?.trend || 'stable',
        weeks_in_state: alert?.weeks_in_state || 0
      }));

      const counts = alertData?.counts || { critical: 0, high: 0, medium: 0, total: 0 };
      const details = {
        cardsLabels: ["Critical", "Warning", "Info"],
        criticalAlerts: counts.critical || alertData?.critical_count || 0,
        highAlerts: counts.warning || alertData?.warning_count || 0,
        mediumAlerts: counts.info || alertData?.info_count || 0,
        alertsList: alertsList,
        listLabel: alertsList.length > 0 ? `${alertsList.length} Alert${alertsList.length > 1 ? 's' : ''} Requiring Attention` : 'No alerts at this time',
        label: `Total Alerts: ${counts.total || 0}`,
        subLabel: metric?.description || '',
        bottomDescription: alertsList.length > 0 ? 'Review and address critical alerts immediately' : 'All participants are performing well',
        total_alerts: alertsList.length,
        tooltipDescription: 'Alert participants who need immediate attention based on performance metrics.',
        points: [
          `Alerts are generated for participants whose performance metrics fall below defined thresholds.`,
          `Severity levels help prioritize attention: Critical, High, Medium.`,
          `Suggested actions provide guidance on how to address performance issues.`
        ]
      };

      return { payload: alertsList, details };
    }
  },
  {
    key: 'performance_trend',
    graph: 'PercentageLineChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'performance_trend');
      const trendData = metric?.data;
      
      if (!trendData) {
        return { payload: [], details: {} };
      }

      // Transform participants data into PercentageLineChart format
      const payload = (trendData?.participants || []).map((participant: any) => {
        const score = participant.current_score || 0;
        const percentage = Math.min(100, Math.max(0, score));
        
        // Determine color based on performance and trend
        let colorFrom = '#3b82f6'; // blue for average
        let colorTo = '#60a5fa';
        
        if (score >= 70) {
          colorFrom = '#22c55e'; // green for high performers
          colorTo = '#16a34a';
        } else if (score < 50) {
          colorFrom = '#ef4444'; // red for low performers
          colorTo = '#dc2626';
        }
        
        // Build info string with trend direction and highlight
        const trendIcon = participant.trend_direction === 'up' ? '↑' : 
                         participant.trend_direction === 'down' ? '↓' : '→';
        const changePct = participant.change_pct || 0;
        const changeStr = changePct !== 0 ? `${changePct >= 0 ? '+' : ''}${changePct.toFixed(1)}%` : 'Stable';
        const highlightStr = participant.highlight ? ` • ${participant.highlight}` : '';
        
        const info = `${trendIcon} ${changeStr}${highlightStr} • Score: ${score.toFixed(1)}`;
        
        return {
          topic: participant.name,
          participant_id: participant.participant_id || '',
          percentage: percentage,
          colorFrom: colorFrom,
          colorTo: colorTo,
          info: info
        };
      });

      // Sort by current score descending
      payload.sort((a: any, b: any) => b.percentage - a.percentage);

      const details = {
        ...trendData,
        period_labels: trendData.period_labels || [],
        top_performer: trendData.top_performer || null,
        most_improved: trendData.most_improved || null,
        most_declined: trendData.most_declined || null,
        warnings: trendData.warnings || [],
        summary_description: trendData.summary_description || '',
        label: `Tracked ${trendData?.participants?.length || 0} participants over ${trendData?.period_labels?.length || 0} periods`,
        subLabel: trendData.summary_description || '',
        bottomDescription: trendData.warnings?.length > 0 ? `Note: ${trendData.warnings[0]}` : ''
      };

      return { payload, details };
    }
  },
  {
    key: 'top_performers',
    graph: 'PercentageLineChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'top_performers');
      const performerData = metric?.data;
      
      if (!performerData) {
        return { payload: [], details: {} };
      }

      const payload = (performerData?.performers || []).map((performer: any) => {
        const score = performer.score || 0;
        const percentage = Math.min(100, Math.max(0, score));
        
        // Determine color gradient based on score
        let colorFrom = '#ef4444'; // red for low score
        let colorTo = '#dc2626';
        
        if (score >= 70) {
          colorFrom = '#22c55e'; // green for high score
          colorTo = '#16a34a';
        } else if (score >= 50) {
          colorFrom = '#f59e0b'; // orange for medium score
          colorTo = '#d97706';
        }
        
        // Build info string with best_in skills and highlight stat
        const bestInSkills = (performer.best_in || []).join(', ');
        const info = `${performer.highlight_stat || ''} | Best in: ${bestInSkills}`;
        
        return {
          topic: `#${performer.rank} ${performer.name}`,
          percentage: percentage,
          colorFrom: colorFrom,
          colorTo: colorTo,
          info: info
        };
      });

      const details = {
        ...performerData,
        period: performerData.period || 'This Period'
      };

      return { payload, details };
    }
  },
  {
    key: 'skill_gap_analysis',
    graph: 'RadarChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'skill_gap_analysis');
      const skillData = metric?.data;
      
      if (!skillData) {
        return { payload: [], details: {} };
      }

      // Transform radar_data for the chart
      const payload = (skillData?.radar_data || []).map((item: any) => ({
        subject: item.skill || '',
        team: item.team || 0,
        benchmark: item.benchmark || 0,
        fullMark: 100
      }));

      const details = {
        ...skillData,
        biggest_gap: skillData.biggest_gap || '',
        strongest_skill: skillData.strongest_skill || ''
      };

      return { payload, details };
    }
  },
  {
    key: 'workload_distribution',
    graph: 'Histogram',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'workload_distribution');
      const workloadData = metric?.data;
      
      if (!workloadData) {
        return { payload: [], details: {} };
      }

      // Transform buckets data for histogram
      const payload = (workloadData?.buckets || []).map((bucket: any) => ({
        range: bucket.range_label || '',
        count: bucket.count || 0,
        percentage: bucket.percentage || 0,
        participant_names: bucket.participant_names || []
      }));

      const details = {
        ...workloadData,
        summary_description: workloadData.summary_description || '',
        tooltip_info: workloadData.tooltip_info || {}
      };

      return { payload, details };
    }
  },
  {
    key: 'customer_satisfaction',
    graph: 'ScatterChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'customer_satisfaction');
      const csatData = metric?.data;
      
      if (!csatData) {
        return { payload: [], details: {} };
      }

      // Transform scatter_points data
      const points = (csatData?.scatter_points || []).map((point: any) => ({
        participant_id: point?.participant_id || '',
        name: point?.name || '',
        quality_score: point?.quality_score || 0,
        csat_score: point?.csat_score || 0,
        sentiment_score: point?.sentiment_score || 0,
        needs_attention: point?.needs_attention || false,
        tooltip_text: point?.tooltip_text || '',
        zone: point?.needs_attention ? 'attention' : 'normal'
      }));

      const details = {
        ...csatData,
        correlation: csatData.correlation || 0,
        correlation_strength: csatData.correlation_strength || '',
        insight: csatData.insight || '',
        summary_description: csatData.summary_description || ''
      };

      return { payload: points, details };
    }
  },
  {
    key: 'improvement_velocity',
    graph: 'PercentageLineChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'improvement_velocity');
      const velocityData = metric?.data;
      
      if (!velocityData) {
        return { payload: [], details: {} };
      }

      // Transform entries into PercentageLineChart format
      const payload = (velocityData?.entries || []).map((entry: any) => {
        const score = entry.current_score || 0;
        const percentage = Math.min(100, Math.max(0, score));
        const changePct = entry.change_pct || 0;
        
        // Determine color based on velocity category and performance
        let colorFrom = '#3b82f6'; // blue for new/stable
        let colorTo = '#60a5fa';
        
        switch (entry.velocity_category) {
          case 'fast':
            colorFrom = '#22c55e'; // green for fast improvers
            colorTo = '#16a34a';
            break;
          case 'steady':
            colorFrom = '#10b981'; // teal for steady
            colorTo = '#059669';
            break;
          case 'plateau':
            colorFrom = '#f59e0b'; // orange for plateau
            colorTo = '#d97706';
            break;
          case 'declining':
            colorFrom = '#ef4444'; // red for declining
            colorTo = '#dc2626';
            break;
          case 'new':
            colorFrom = '#6366f1'; // indigo for new
            colorTo = '#4f46e5';
            break;
          default:
            colorFrom = entry.status_color || '#3b82f6';
            colorTo = entry.status_color || '#60a5fa';
        }
        
        // Build arrows based on arrow_count and direction
        let arrowSymbol = '';
        const arrowCount = entry.arrow_count || 0;
        if (entry.arrow_direction === 'up') {
          arrowSymbol = '↑'.repeat(Math.min(arrowCount, 3));
        } else if (entry.arrow_direction === 'down') {
          arrowSymbol = '↓'.repeat(Math.min(arrowCount, 3));
        } else {
          arrowSymbol = '→';
        }
        
        // Build comprehensive info string
        const velocityCategoryLabel = entry.velocity_category ? ` • ${entry.velocity_category.charAt(0).toUpperCase() + entry.velocity_category.slice(1)}` : '';
        const weeksTracked = entry.weeks_tracked > 0 ? ` • ${entry.weeks_tracked} weeks tracked` : ' • New participant';
        const changeStr = changePct !== 0 ? `${changePct >= 0 ? '+' : ''}${changePct.toFixed(1)}%` : 'No change';
        const scoreInfo = `Score: ${score.toFixed(1)}`;
        
        let info = `${arrowSymbol} ${changeStr}${velocityCategoryLabel}${weeksTracked} • ${scoreInfo}`;
        
        // Add previous score if available
        if (entry.previous_score && entry.previous_score > 0) {
          info += ` (was ${entry.previous_score.toFixed(1)})`;
        }
        
        return {
          topic: entry.name,
          percentage: percentage,
          colorFrom: colorFrom,
          colorTo: colorTo,
          info: info
        };
      });

      // Sort by current score descending
      payload.sort((a: any, b: any) => b.percentage - a.percentage);

      const details = {
        ...velocityData,
        avg_improvement: velocityData.avg_improvement || 0,
        fast_improvers: velocityData.fast_improvers || 0,
        declining_count: velocityData.declining_count || 0,
        new_count: velocityData.new_count || 0,
        comparison_summary: velocityData.comparison_summary || {},
        declined_participants: velocityData.declined_participants || [],
        summary_description: velocityData.summary_description || '',
        label: `Average Improvement: ${velocityData.avg_improvement || 0}% • Fast Improvers: ${velocityData.fast_improvers || 0} • Declining: ${velocityData.declining_count || 0} • New: ${velocityData.new_count || 0}`,
        subLabel: velocityData.summary_description || '',
        bottomDescription: velocityData.declined_participants?.length > 0 
          ? `Declining participants need attention: ${velocityData.declined_participants.join(', ')}` 
          : 'Continue monitoring participant progress over time'
      };

      return { payload, details };
    }
  },
  {
    key: 'period_comparison',
    graph: 'CompareInfoCards',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'period_comparison');
      const comparisonData = metric?.data;
      
      if (!comparisonData) {
        return { payload: [], details: {} };
      }

      const payload = (comparisonData?.cards || []).map((card: any) => {
        // Determine label based on change direction
        let label = 'Stable';
        let targetSubLine = 'No Change';
        
        if (card.change_direction === 'up') {
          label = card.is_positive_change ? 'Improved' : 'Increased';
          targetSubLine = card.is_positive_change ? 'Positive Trend' : 'Above Previous';
        } else if (card.change_direction === 'down') {
          label = card.is_positive_change ? 'Decreased' : 'Declined';
          targetSubLine = card.is_positive_change ? 'Positive Trend' : 'Below Previous';
        }

        // Format score based on format_type
        let formattedScore = card.current_value?.toString() || '0';
        if (card.format_type === 'percent') {
          formattedScore = `${card.current_value}%`;
        }

        return {
          cardTitle: card.metric_name || '',
          score: formattedScore,
          lable: label,
          targetLine: card.target_label || 'No Target',
          targetSubLine: targetSubLine,
          description: `Change: ${card.change_pct >= 0 ? '+' : ''}${card.change_pct}%`
        };
      });

      const details = {
        ...comparisonData,
        current_period: comparisonData.current_period || '',
        previous_period: comparisonData.previous_period || '',
        overall_trend: comparisonData.overall_trend || '',
        summary_description: comparisonData.summary_description || ''
      };

      return { payload, details };
    }
  }
];